import React from 'react';

import { Variable } from '../../common/Variable';
import type { MailTemplate } from '../components/SciMailTemplates';

type MailTemplateContent = Omit<MailTemplate, 'content'> & { content: React.ReactNode };

export const rentMailTemplates: MailTemplateContent[] = [
  {
    id: 1,
    titre: 'SCI / proposition service gestion locative / général',
    apercu: 'Proposition de gestion locative adaptée à votre SCI...',
    objet: 'Proposition de gestion locative adaptée à votre SCI',
    content: (
      <div className="space-y-4">
        <div>
          <p className="font-semibold text-gray-700">Objet :</p>
          <p className="mt-1">Proposition de gestion locative adaptée à votre SCI</p>
        </div>

        <div className="space-y-4 pt-4">
          <p>Bonjour,</p>

          <p>
            Votre SCI possède plusieurs biens dans la copropriété « <strong>[NOM_DE_LA_COPRO]</strong> » située au <strong>[ADRESSE_DE_LA_COPRO]</strong>, immeuble que nous gérons en qualité de syndic de copropriété.
          </p>

          <p>
            Afin de vous permettre une gestion locative sereine, efficace et optimisée de votre patrimoine immobilier, nous souhaitons vous proposer les services de notre équipe dédiée à cette activité.
          </p>

          <p>Notre offre inclut notamment :</p>

          <ul className="ml-6 list-disc space-y-2">
            <li>L'analyse de rendement locatif actuel et l'évaluation de votre bien comme préalable en tenant compte de la vie de l'immeuble, du futur DPE collectif et des éventuels travaux à prévoir dans le cadre du PPPT.</li>
            <li>Une analyse de la fiscalité actuelle et des différentes possibilités en fonction de votre stratégie patrimoniale</li>
          </ul>

          <p>Puis au quotidien :</p>

          <ul className="ml-6 list-disc space-y-2">
            <li>Recherche et sélection rigoureuse des locataires,</li>
            <li>Gestion administrative complète,</li>
            <li>Suivi technique régulier des biens,</li>
            <li>Optimisation des loyers pour assurer un rendement attractif.</li>
          </ul>

          <p>
            Nous serions ravis de vous présenter plus en détail notre accompagnement sur mesure lors d'un entretien à votre convenance.
          </p>

          <p>Restant à votre disposition,</p>
          <p>Bien cordialement,</p>

          <p>
            [Votre Nom]
            <br />
            Responsable Gestion Locative
            <br />
            [Téléphone]
            <br />
            [Email]
          </p>

          <p className="mt-6 text-xs text-gray-600 italic">
            Ce message vous est adressé par <Variable>[NOM_SOUSTRAITANT_MAIL]</Variable>, <Variable>[ADRESSE_SOUSTRAITANT_MAIL]</Variable>, <Variable>[REPRESENTANT_LEGAL_SOUSTRAITANT_MAIL]</Variable>, <Variable>[CONTACT_SOUSTRAITANT_MAIL]</Variable>. Si vous souhaitez ne plus recevoir d'email concernant l'objet précis de cet envoi, vous pouvez vous désinscrire <Variable>[LIEN_DE_DESINSCRIPTION]</Variable>.
          </p>
        </div>
      </div>
    ),
  },
  {
    id: 2,
    titre: 'SCI / proposition service gestion locative / général',
    apercu: 'Confiez la gestion locative de vos biens à un expert...',
    objet: 'Confiez la gestion locative de vos biens à un expert qui connaît votre copropriété',
    content: (
      <div className="space-y-4">
        <div>
          <p className="font-semibold text-gray-700">Objet :</p>
          <p className="mt-1">Confiez la gestion locative de vos biens à un expert qui connaît votre copropriété</p>
        </div>

        <div className="space-y-4 pt-4">
          <p>Bonjour,</p>

          <p>
            En tant que syndic de la copropriété « <strong>[NOM_DE_LA_COPRO]</strong> », située au <strong>[ADRESSE_DE_LA_COPRO]</strong>, nous connaissons parfaitement les particularités et le fonctionnement de votre immeuble.
          </p>

          <p>
            Forts de cette expertise unique, nous vous proposons un service de gestion locative personnalisé et performant qui vous permettra de sécuriser votre investissement, d'optimiser votre rentabilité locative, et de simplifier la gestion quotidienne de vos biens.
          </p>

          <p>Nos services incluent notamment :</p>

          <ul className="ml-6 list-disc space-y-2">
            <li>Sélection rigoureuse et sécurisée de vos locataires,</li>
            <li>Suivi administratif et technique complet,</li>
            <li>Optimisation des loyers et conseils adaptés à l'évolution du marché immobilier local,</li>
            <li>Anticipation des travaux et gestion efficace des éventuelles interventions grâce à notre connaissance approfondie du PPPT et du DPE collectif.</li>
          </ul>

          <p>
            Nous serions ravis d'échanger avec vous afin de vous présenter en détail comment notre gestion locative pourrait concrètement valoriser votre patrimoine immobilier.
          </p>

          <p>Restant à votre entière disposition,</p>
          <p>Bien cordialement,</p>

          <p>
            [Votre Nom]
            <br />
            Responsable Gestion Locative
            <br />
            [Téléphone]
            <br />
            [Email]
          </p>

          <p className="mt-6 text-xs text-gray-600 italic">
            Ce message vous est adressé par <Variable>[NOM_SOUSTRAITANT_MAIL]</Variable>, <Variable>[ADRESSE_SOUSTRAITANT_MAIL]</Variable>, <Variable>[REPRESENTANT_LEGAL_SOUSTRAITANT_MAIL]</Variable>, <Variable>[CONTACT_SOUSTRAITANT_MAIL]</Variable>. Si vous souhaitez ne plus recevoir d'email concernant l'objet précis de cet envoi, vous pouvez vous désinscrire <Variable>[LIEN_DE_DESINSCRIPTION]</Variable>.
          </p>
        </div>
      </div>
    ),
  },
  {
    id: 3,
    titre: 'SCI / PARTICULIER information PNO / général',
    apercu: 'Obligations des copropriétaires bailleurs (Assurance PNO et règlement)...',
    objet: 'Obligations des copropriétaires bailleurs - Assurance PNO et règlement de copropriété',
    content: (
      <div className="space-y-4">
        <div>
          <p className="font-semibold text-gray-700">Objet :</p>
          <p className="mt-1">Obligations des copropriétaires bailleurs - Assurance PNO et règlement de copropriété</p>
        </div>

        <div className="space-y-4 pt-4">
          <p>Bonjour,</p>

          <p>
            Nous vous rappelons qu'en tant que copropriétaire bailleur, vous avez l'obligation de souscrire une assurance Propriétaire Non Occupant (PNO) et de fournir à votre locataire un extrait du règlement de copropriété concernant les règles d'usage des parties privatives et communes.
          </p>

          <p>Cette obligation est définie notamment par les articles suivants :</p>

          <ul className="ml-6 list-disc space-y-2">
            <li>Article 9-1 de la loi n° 65-557 du 10 juillet 1965 fixant le statut de la copropriété des immeubles bâtis</li>
            <li>Article 7 de la loi n° 89-462 du 6 juillet 1989 tendant à améliorer les rapports locatifs et portant modification de la loi n° 86-1290 du 23 décembre 1986</li>
          </ul>

          <p>
            Afin de mieux vous accompagner dans ces démarches obligatoires et vous informer précisément sur vos obligations légales, nous avons créé un nouveau poste dédié. Votre référent Mutation-Location est désormais Monsieur X, disponible pour répondre à toutes vos questions et vous guider efficacement.
          </p>

          <p>N'hésitez pas à le contacter pour toute information complémentaire.</p>

          <p>Bien cordialement,</p>

          <p>
            [Votre Nom]
            <br />
            Syndic de copropriété
            <br />
            [Téléphone]
            <br />
            [Email]
          </p>

          <p className="mt-6 text-xs text-gray-600 italic">
            Ce message vous est adressé par <Variable>[NOM_SOUSTRAITANT_MAIL]</Variable>, <Variable>[ADRESSE_SOUSTRAITANT_MAIL]</Variable>, <Variable>[REPRESENTANT_LEGAL_SOUSTRAITANT_MAIL]</Variable>, <Variable>[CONTACT_SOUSTRAITANT_MAIL]</Variable>. Si vous souhaitez ne plus recevoir d'email concernant l'objet précis de cet envoi, vous pouvez vous désinscrire <Variable>[LIEN_DE_DESINSCRIPTION]</Variable>.
          </p>
        </div>
      </div>
    ),
  },
];
